// ConversationPicker — the chat panel's conversation head, pure.
//
// The current conversation's title (a trigger opening the recent
// conversations) and "New"; the panel puts its "Conversation" label above. The title truncates to
// one line; the list scrolls; an empty list says so; a failed read offers a
// retry. Picking a row reports it up and closes the list.
//
// The list's open/closed state is view-ephemeral (survives no reload, carries
// no domain meaning), so it is local state, like every popover's.

export { ConversationPicker };

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { Popover } from "../Popover/Popover.js";

type ConversationPickerRow = {
    readonly id: string;
    readonly title: string;
    readonly when?: string;
    readonly active: boolean;
};

type ConversationPickerProps = {
    // The shown conversation's title ("New conversation" before it has one).
    readonly title: string;
    readonly status: "pending" | "failed" | "ready";
    readonly rows: readonly ConversationPickerRow[];
    readonly onOpen: (id: string) => void;
    readonly onNew: () => void;
    readonly onRetry: () => void;
};

const ConversationPicker = ({
    title,
    status,
    rows,
    onOpen,
    onNew,
    onRetry,
}: ConversationPickerProps): ReactNode => {
    const { t } = useLingui();
    const [open, setOpen] = useState(false);
    const list: ReactNode =
        status === "pending" ? null : status === "failed" ? (
            <div className="bilda-chat-panel__note" role="status">
                <span>{t`Couldn't load conversations`}</span>
                <button
                    type="button"
                    className="bilda-chat-panel__retry"
                    onClick={onRetry}
                >
                    {t`Try again`}
                </button>
            </div>
        ) : rows.length === 0 ? (
            <p className="bilda-chat-panel__note">{t`No conversations yet`}</p>
        ) : (
            <div className="bilda-chat-panel__picker-list">
                {rows.map((row) => (
                    <button
                        key={row.id}
                        type="button"
                        className="bilda-chat-panel__picker-row"
                        aria-current={row.active ? "true" : undefined}
                        data-active={row.active ? "true" : undefined}
                        title={row.title}
                        onClick={() => {
                            setOpen(false);
                            onOpen(row.id);
                        }}
                    >
                        <span className="bilda-chat-panel__picker-title">
                            {row.title}
                        </span>
                        {row.when !== undefined ? (
                            <span className="bilda-chat-panel__picker-when">
                                {row.when}
                            </span>
                        ) : null}
                    </button>
                ))}
            </div>
        );
    return (
        <div className="bilda-chat-panel__head-row">
            <Popover
                open={open}
                onOpenChange={setOpen}
                side="bottom"
                align="start"
                label={t`Recent conversations`}
                trigger={
                    <button
                        type="button"
                        className="bilda-chat-panel__picker"
                        aria-label={t`Switch conversation: ${title}`}
                    >
                        <span className="bilda-chat-panel__picker-title">
                            {title}
                        </span>
                        <Icon name="chevron-down" />
                    </button>
                }
            >
                <div className="bilda-chat-panel__picker-panel">
                    <h3 className="bilda-chat-panel__picker-heading">
                        {t`Recent`}
                    </h3>
                    {list}
                </div>
            </Popover>
            <Button
                variant="secondary"
                size="md"
                className="bilda-chat-panel__new"
                onClick={onNew}
            >
                {t`New`}
                <Icon name="plus" size="sm" />
            </Button>
        </div>
    );
};
